import { useCallback, useEffect, useRef } from "react";

/**
 * Preserve a MUI DataGrid's scroll position across data refetches.
 *
 * The grid re-renders in several passes after its `rows` prop changes
 * (loading overlay unmount, row re-measure, virtualizer reset) and a bare
 * `scrollTop` write moves the scrollbar while the virtualizer keeps rendering
 * from index 0 (thumb down, rows up). This hook solves that by:
 *
 * 1. `capture()` — called BEFORE the refetch: saves the virtual scroller's
 *    pixel offset AND the first VISIBLE row id (the first DOM row can be an
 *    off-screen buffer row above the viewport).
 * 2. `restore(rows)` — called AFTER the new rows land: anchors back to that
 *    same row via the grid API (`scrollToIndexes` materializes it once, then
 *    small rect-delta nudges with a settle frame between write and measure —
 *    measuring on the same frame as the write reads stale layout and causes
 *    up/down jitter), with a pixel + synthetic-scroll-event fallback. Stops
 *    as soon as the anchor is aligned, when a correction stops reducing the
 *    error, or on user interaction.
 *
 * Everything is scoped to THIS grid's root element via its apiRef — a global
 * `document.querySelector` can match a DIFFERENT grid mounted elsewhere on
 * the page.
 *
 * Usage:
 *   const apiRef = useGridApiRef();
 *   const { capture, restore } = useDataGridScrollRestore(apiRef);
 *   // inside the fetch function, before the request:
 *   capture();
 *   // after setRows(...):
 *   restore(rows);
 *
 * @param {Object} apiRef - The DataGrid apiRef (from useGridApiRef()).
 * @returns {{ capture: Function, restore: Function }}
 */
export const useDataGridScrollRestore = (apiRef) => {
  const scrollTopRef = useRef(0);
  const anchorRowIdRef = useRef(null);

  // Cancel any in-flight restore animation when the component unmounts.
  const cancelRef = useRef(false);
  useEffect(() => {
    cancelRef.current = false;
    return () => {
      cancelRef.current = true;
    };
  }, []);

  const getScroller = useCallback(() => {
    // Only ever look inside THIS grid's root. A document-level fallback can
    // match a DIFFERENT grid on multi-grid pages (dashboards stack several),
    // which would capture/restore the wrong scroller.
    const root = apiRef?.current?.rootElementRef?.current;
    if (!root) return null;
    return root.querySelector(".MuiDataGrid-virtualScroller");
  }, [apiRef]);

  const capture = useCallback(() => {
    const scroller = getScroller();
    // Always refresh BOTH refs — a stale pixel offset from a fetch that never
    // reached restore() must not leak into a later capture taken at the top.
    scrollTopRef.current =
      scroller && scroller.scrollTop > 0 ? scroller.scrollTop : 0;

    // Only anchor by row when the user was actually scrolled down — at the
    // top we must stay at the top (a new row inserted above would otherwise
    // yank the list). Pick the first row actually VISIBLE in the viewport.
    let firstVisibleRowEl = null;
    if (scroller && scroller.scrollTop > 0) {
      const scRect = scroller.getBoundingClientRect();
      const rowEls = scroller.querySelectorAll(".MuiDataGrid-row[data-id]");
      for (const el of rowEls) {
        if (el.getBoundingClientRect().bottom > scRect.top + 1) {
          firstVisibleRowEl = el;
          break;
        }
      }
    }
    anchorRowIdRef.current = firstVisibleRowEl
      ? firstVisibleRowEl.getAttribute("data-id")
      : null;
  }, [getScroller]);

  /**
   * Restore the captured position once the new rows are rendered.
   * @param {Array} rows - The new rows array (to locate the anchor row index).
   */
  const restore = useCallback(
    (rows) => {
      const savedRowId = anchorRowIdRef.current;
      const savedPixel = scrollTopRef.current;
      if (savedRowId == null && savedPixel <= 0) return;

      const targetIdx =
        savedRowId != null && Array.isArray(rows)
          ? rows.findIndex((r) => String(r?.id) === String(savedRowId))
          : -1;

      let cancelled = false;
      const cancelOnInteraction = () => {
        cancelled = true;
      };
      const interactionEvents = ["wheel", "mousedown", "touchstart"];
      const initialScroller = getScroller();
      interactionEvents.forEach((evt) => {
        initialScroller?.addEventListener(evt, cancelOnInteraction, {
          once: true,
          passive: true,
        });
      });

      const cleanup = () => {
        scrollTopRef.current = 0;
        anchorRowIdRef.current = null;
        const sc = getScroller();
        interactionEvents.forEach((evt) => {
          sc?.removeEventListener(evt, cancelOnInteraction);
        });
      };

      let materialized = targetIdx < 0;
      let settleFrames = 0;
      let attempts = 0;
      let lastAbsDelta = null;
      const maxAttempts = 40;

      const step = () => {
        if (cancelled || cancelRef.current) {
          cleanup();
          return;
        }

        const sc = getScroller();
        if (!sc) {
          attempts += 1;
          if (attempts < maxAttempts) requestAnimationFrame(step);
          else cleanup();
          return;
        }

        // Phase 1 — bring the anchor row into the render window once.
        if (!materialized) {
          apiRef?.current?.scrollToIndexes?.({ rowIndex: targetIdx });
          materialized = true;
          settleFrames = 1;
          requestAnimationFrame(step);
          return;
        }

        // Let the grid apply the last write before measuring again.
        if (settleFrames > 0) {
          settleFrames -= 1;
          requestAnimationFrame(step);
          return;
        }

        if (targetIdx >= 0) {
          const rowEl = sc.querySelector(
            `.MuiDataGrid-row[data-id="${savedRowId}"]`,
          );
          if (!rowEl) {
            // Virtualizer still settling — wait (bounded).
            attempts += 1;
            if (attempts < maxAttempts) requestAnimationFrame(step);
            else cleanup();
            return;
          }

          const delta =
            rowEl.getBoundingClientRect().top - sc.getBoundingClientRect().top;
          const absDelta = Math.abs(delta);
          if (absDelta <= 2) {
            cleanup();
            return;
          }
          // If the previous nudge did not reduce the error, stop —
          // re-asserting would just jitter between two positions.
          if (lastAbsDelta !== null && absDelta >= lastAbsDelta - 1) {
            cleanup();
            return;
          }
          lastAbsDelta = absDelta;
          sc.scrollTop += delta;
          settleFrames = 1;
        } else {
          // No usable row anchor — restore the pixel offset and nudge the
          // virtualizer with a scroll event (a no-op scrollTop write fires
          // no event, so the render window never updates).
          const pxDiff = Math.abs(sc.scrollTop - savedPixel);
          if (pxDiff <= 2) {
            cleanup();
            return;
          }
          if (lastAbsDelta !== null && pxDiff >= lastAbsDelta - 1) {
            cleanup();
            return;
          }
          lastAbsDelta = pxDiff;
          sc.scrollTop = savedPixel;
          sc.dispatchEvent(new Event("scroll"));
          settleFrames = 1;
        }

        attempts += 1;
        if (attempts < maxAttempts && !cancelled) {
          requestAnimationFrame(step);
        } else {
          cleanup();
        }
      };

      requestAnimationFrame(step);
    },
    [apiRef, getScroller],
  );

  return { capture, restore };
};

export default useDataGridScrollRestore;
